import { useState } from 'react';
import {
  useTranslate,
  useNotification,
  useCustomMutation,
  useInvalidate,
} from '@refinedev/core';
import {
  Button,
  Checkbox,
  Dropdown,
  Flex,
  Modal,
  Typography,
  theme,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import type {
  ContentProductBulkActionIn,
  ContentProductBulkActionOut,
} from '@/api/generated/schemas';
import { ProductBulkAction } from '@/api/generated/schemas';
import { extractBackendMessage } from '@/utils/apiError';
import { ProductBulkActionResult } from './ProductBulkActionResult';

const { Text } = Typography;

/** The endpoint caps one call at this many products. */
const MAX_PRODUCTS = 100;

/** `force` only means something for transcoding (re-run an already transcoded product). */
const supportsForce = (action: ProductBulkAction) =>
  action === ProductBulkAction.reconcile_transcoding;

interface ProductBulkActionsProps {
  /** Content-catalog product ids the action applies to. */
  ids: number[];
  /** Actions offered in the dropdown; all of them by default. */
  actions?: ProductBulkAction[];
  /** Product titles by id, for the per-product outcome list. */
  titles?: Record<number, string>;
  /** A run finished; the list has been invalidated. The modal stays open. */
  onDone?: () => void;
  /**
   * The user closed the modal after a finished run — the safe point to
   * clear the table selection (clearing it earlier would unmount this
   * control and the open result view with it).
   */
  onClosed?: () => void;
  /** The modal opened or closed; keep this control mounted while open. */
  onOpenChange?: (open: boolean) => void;
}

type Phase = 'confirm' | 'running' | 'done';

export const ProductBulkActions = ({
  ids,
  actions = Object.values(ProductBulkAction),
  titles,
  onDone,
  onClosed,
  onOpenChange,
}: ProductBulkActionsProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation<ContentProductBulkActionOut>();

  const [action, setAction] = useState<ProductBulkAction | null>(null);
  const [force, setForce] = useState(false);
  const [ignoreGate, setIgnoreGate] = useState(false);
  const [phase, setPhase] = useState<Phase>('confirm');
  const [result, setResult] = useState<ContentProductBulkActionOut | null>(
    null
  );
  // The run acts on what was selected when it started: the refetch after a
  // run can drop rows out of the view, and a live `ids` would shrink with it.
  const [runIds, setRunIds] = useState<number[]>([]);
  // Same for the titles: they come from the live rows, which the refetch
  // can drop while the result view is still open.
  const [runTitles, setRunTitles] = useState<Record<number, string>>({});

  const tk = (suffix: string, options?: Record<string, unknown>) =>
    t(`content_catalog.products.bulk.${suffix}`, options);
  const actionLabel = (a: ProductBulkAction) => tk(`actions.${a}`);

  const open = (a: ProductBulkAction) => {
    setForce(false);
    setIgnoreGate(false);
    setPhase('confirm');
    setResult(null);
    setRunIds(ids.slice(0, MAX_PRODUCTS));
    setRunTitles(titles ?? {});
    setAction(a);
    onOpenChange?.(true);
  };

  const close = () => {
    if (phase === 'running') return;
    const finished = phase === 'done';
    setAction(null);
    onOpenChange?.(false);
    if (finished) onClosed?.();
  };

  const run = async () => {
    if (!action) return;
    setPhase('running');
    const payload: ContentProductBulkActionIn = {
      action,
      product_ids: runIds,
      ...(supportsForce(action) ? { force } : {}),
      ignore_distribution_gate: ignoreGate,
    };
    try {
      const { data } = await mutateAsync({
        url: 'content_catalog/products/bulk_actions',
        method: 'post',
        values: payload,
        successNotification: false,
        errorNotification: false,
      });
      setResult(data);
      setPhase('done');
      invalidate({
        resource: 'content_catalog.products',
        invalidates: ['list'],
      });
      onDone?.();
    } catch (error) {
      notify?.({
        type: 'error',
        message: actionLabel(action),
        description: extractBackendMessage(error) ?? tk('error_fallback'),
      });
      // Back to the confirmation so the user can retry or walk away.
      setPhase('confirm');
    }
  };

  const running = phase === 'running';

  return (
    <>
      <Dropdown
        trigger={['click']}
        disabled={ids.length === 0}
        menu={{
          items: actions.map((value) => ({
            key: value,
            label: actionLabel(value),
          })),
          onClick: ({ key }) => open(key as ProductBulkAction),
        }}
      >
        <Button type="primary">
          {tk('apply')} <DownOutlined />
        </Button>
      </Dropdown>

      <Modal
        open={action != null}
        title={action ? actionLabel(action) : undefined}
        closable={!running}
        keyboard={!running}
        maskClosable={false}
        onCancel={close}
        destroyOnClose
        width={720}
        footer={
          <Flex align="center" gap={token.marginSM}>
            <Text
              type="secondary"
              style={{ flex: 1, minWidth: 0, textAlign: 'left' }}
            >
              {phase === 'done' && result
                ? tk('summary', {
                    enqueued: result.enqueued,
                    nothing_to_do: result.nothing_to_do,
                    blocked: result.blocked,
                    failed: result.failed,
                    unknown: result.unknown_product,
                  })
                : runIds.length === 1
                  ? tk('confirm_single')
                  : tk('confirm', { count: runIds.length })}
            </Text>
            {phase === 'done' ? (
              <Button type="primary" onClick={close}>
                {t('actions.close')}
              </Button>
            ) : (
              <>
                <Button onClick={close} disabled={running}>
                  {t('actions.cancel')}
                </Button>
                <Button type="primary" loading={running} onClick={run}>
                  {running ? tk('running') : tk('run')}
                </Button>
              </>
            )}
          </Flex>
        }
      >
        {phase === 'done' && result ? (
          <ProductBulkActionResult result={result} titles={runTitles} />
        ) : (
          <Flex vertical gap={token.marginSM}>
            {ids.length > MAX_PRODUCTS && (
              <Text type="warning">
                {tk('capped', { max: MAX_PRODUCTS, total: ids.length })}
              </Text>
            )}
            {action && supportsForce(action) && (
              <Checkbox
                checked={force}
                disabled={running}
                onChange={(e) => setForce(e.target.checked)}
              >
                {tk('force')}
              </Checkbox>
            )}
            <Checkbox
              checked={ignoreGate}
              disabled={running}
              onChange={(e) => setIgnoreGate(e.target.checked)}
            >
              {tk('ignore_gate')}
            </Checkbox>
            {running && <Text type="secondary">{tk('running_hint')}</Text>}
          </Flex>
        )}
      </Modal>
    </>
  );
};
